import { forwardRef, useCallback, useEffect, useRef } from "react";
import VideoJsPlayer from "./Video";
import { noop } from "@helpers/common";

/**
 * Player component for rendering a media player.
 *
 * @param {Object} props - The props object.
 * @param {string[]} props.sources - Array of source URLs for the media player.
 * @param {string} [props.poster] - URL of the image to be shown before the video plays.
 * @param {string} [props.className] - Additional class names to apply to the player component.
 * @param {boolean} [props.landing] - Whether the player is on a landing page.
 * @param {boolean} [props.fluid] - Whether the player should have a fluid layout.
 * @param {boolean} [props.isAudio] - When passed true, the player will have the audio placeholder.
 * @param {Object} [props.style] - Inline styles to apply to the player component.
 * @param {string} [props.preload] - Specifies how the video should be loaded when the page loads. Can be "auto", "metadata", or "none".
 * @param {Function} [props.onReady] - Callback which is being fired when player is ready.
 * @returns {React.Element} The rendered Player component.
 */
const Player = forwardRef(
  (
    {
      sources,
      poster,
      className,
      landing,
      fluid,
      style,
      preload,
      onReady,
      hideFullscreen,
      isAudio,
      setDraggable = noop,
    },
    ref,
  ) => {
    const defaultRef = useRef(null);

    const handleReady = useCallback((player) => {
      defaultRef.current = player;
      onReady ? onReady(player) : onReadyDefault(player);
    }, []);

    const volumeBarRef = useRef();

    const handleDragStart = () => {
      setDraggable(false);
      setTimeout(() => {
        setDraggable(true);
      }, 500);
    };

    const onReadyDefault = (player) => {
      // Save the volume bar element
      volumeBarRef.current =
        player?.controlBar?.volumePanel?.volumeControl?.volumeBar?.el_;

      // Check if the volume bar exists
      if (!volumeBarRef.current) return;
      // Add mousedown event listener to handle the drag start
      volumeBarRef.current.addEventListener("mousedown", handleDragStart);
    };

    useEffect(() => {
      return () => {
        // Clean up the event listener
        if (!volumeBarRef.current) return;
        volumeBarRef.current.removeEventListener("mousedown", handleDragStart);
      };
    }, []);

    return (
      <VideoJsPlayer
        style={style}
        poster={poster}
        onReady={handleReady}
        sources={sources}
        landing={landing}
        className={className}
        fluid={fluid}
        preload={preload}
        hideFullscreen={hideFullscreen}
        ref={ref}
        isAudio={isAudio}
      />
    );
  },
);

Player.displayName = "Player";

export default Player;
